import React, { useContext, useState } from 'react';
import { CellContext, ColumnDefTemplate } from '@tanstack/react-table';
import { Box } from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useOrgNavigate } from 'app/routes/useOrgNavigate';

import ScorecardButtonBase from 'Components/Scorecards/ScorecardTable/ScorecardOptions/ScorecardButtonBase';
import { useAppSelector } from 'hooks/index';
import { selectUserData } from 'store/user/userSelector';
import errorIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
    FirestoreUser,
    FirestoreUserRole,
    useArchiveUserMutation,
} from 'generated/graphql';
import { useNotificationContext } from 'app/contexts/NotificationContext';
import UserContext from '../context/UsersContext';
import OperatorModal from '../../modal/OperatorModal';
import { selectTeamsForTeamSelector } from 'store/partner/partnerSelector';
import getTeamName from '../utils/getTeamName';

export const UserRoleCell: ColumnDefTemplate<
    CellContext<FirestoreUser, FirestoreUserRole[]>
> = ({ getValue }) => {
    const roles = getValue();

    if (roles.includes(FirestoreUserRole.OrganizationAdmin)) {
        return 'Organization Admin';
    }
    return 'User';
};

export const UserTeamCell: ColumnDefTemplate<
    CellContext<FirestoreUser, string>
> = ({ getValue }) => {
    const teamId = getValue();

    const activeTeams = useAppSelector(selectTeamsForTeamSelector);

    return getTeamName(teamId, activeTeams);
};

export const ActionsCell: ColumnDefTemplate<
    CellContext<FirestoreUser, string>
> = ({ row }) => {
    const {
        firstName: rowFirstName,
        email: rowEmail,
        id: rowId,
        uid: rowUid,
    } = row.original;

    const navigate = useOrgNavigate();

    const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);

    const { uid: userUid } = useAppSelector(selectUserData);

    const { showNotification } = useNotificationContext();
    const { removeUser } = useContext(UserContext);

    const [archive, { loading }] = useArchiveUserMutation({
        onCompleted: () => {
            removeUser(rowId);
            showNotification({
                message: 'User has been archived from this organization!',
                severity: 'success',
            });
        },
        onError: () => {
            showNotification({
                message:
                    'There was an error while trying to archive this user.',
                severity: 'error',
            });
        },
    });

    const handleOnClick = async () => {
        await archive({
            variables: {
                userId: rowId,
            },
        });

        setShowArchiveModal(false);
    };

    return (
        <Box display="flex" alignItems="center">
            {userUid !== rowUid && (
                <>
                    <ScorecardButtonBase
                        onClick={() => setShowArchiveModal(true)}
                        startIcon={<ArchiveOutlinedIcon />}
                    />
                    <ScorecardButtonBase
                        onClick={() => {
                            navigate(`user/${rowUid}/edit-user`);
                        }}
                        startIcon={<EditOutlinedIcon />}
                    />
                </>
            )}

            <OperatorModal
                open={showArchiveModal}
                loading={loading}
                setOpen={setShowArchiveModal}
                Icon={errorIcon}
                headline="Are you sure?"
                description={`You are about to archive ${rowFirstName || rowEmail} in Quality, they will lose access from any applications, as well as data associated with them.`}
                okayButtonLabel={`I understand, archive ${rowFirstName || rowEmail}`}
                cancelButtonLabel="No. WAIT. CANCEL, CANCEL"
                customColor="rgb(182, 0, 0)"
                onClick={handleOnClick}
            />
        </Box>
    );
};
